import _ from 'lodash';
import { getAtKeypath } from 'services/keypath';
import { RootState, useAppSelector } from './store';
import {
  Admin,
  BookingFlowOption,
  Manager,
  MedicationReminderNotification,
  PatientMedication,
  Physician,
  PhysicianPhysicianTypeEnum,
  PortalData,
  UserRoleEnum,
} from 'generated/api';
import { createSelector } from '@reduxjs/toolkit';
import { groupMedicationReminderNotifications, PatientData } from './slices/healthRecords';
import CPPUtils from 'services/CPPUtils';
import { PERMISSIONS } from 'services/StaffUtils';
import config from '../config';
import { getPortalData } from '../services/portalData';

export const AuthenticatedPatient = (state: RootState) => {
  const id = state.authentication.decodedToken.sub;
  return id ? state.patients.byId[id] : undefined;
};

export const ActivePatient = (state: RootState) => {
  const id = state.authentication.activeUserId;
  return id ? state.patients.byId[id] : undefined;
};

export const GetPhysician = (state: RootState, id?: string, shouldFallback = true) => {
  const fallback = id ? undefined : state.authentication.user;
  if (state.authentication.role !== 'physician') {
    return;
  }
  if (!id && shouldFallback) id = state.authentication.activeUserId;
  if (id) {
    return state.physicians.byId[id] || (fallback as Physician | undefined);
  }
};

export const GetStaff = (state: RootState, id?: string, shouldFallback = true) => {
  const fallback = id ? undefined : state.authentication.user;
  if (state.authentication.role === 'patient' || state.authentication.role === 'manager') {
    return;
  }
  if (!id && shouldFallback) id = state.authentication.activeUserId;
  if (id) {
    return (
      state.physicians.byId[id] ||
      state.admins.byId[id] ||
      (fallback as Physician | Admin | undefined)
    );
  }
};

export const ActivePhysician = (state: RootState) => {
  const id = state.authentication.activeUserId;
  if (id) {
    return state.physicians.byId[id] || (state.authentication.user as Physician);
  }
};

export const InternOf = (state: RootState) => {
  let activePhysician = ActivePhysician(state);
  return activePhysician?.intern_of;
};

export const ActiveUserHr = (state: RootState) => {
  if (!state.authentication.activeUserId) {
    return undefined;
  }
  return state.healthRecords.byId[state.authentication.activeUserId];
};

export const IsTypePhlebotomist = (state: RootState) => {
  const userId = state.authentication.activeUserId;
  if (!userId) return false;
  return state.physicians.byId[userId]?.physician_type === PhysicianPhysicianTypeEnum.Phlebotomist;
};

export const IsTypeNurse = (state: RootState) => {
  const userId = state.authentication.activeUserId;
  if (!userId) return false;
  return state.physicians.byId[userId]?.physician_type === PhysicianPhysicianTypeEnum.Nurse;
};

export const IsTypeNP = (state: RootState) => {
  const userId = state.authentication.activeUserId;
  if (!userId) return false;
  let user = state.physicians.byId[userId];
  const isPhysician = state.authentication.role === UserRoleEnum.Physician;
  return (
    isPhysician &&
    ([undefined, PhysicianPhysicianTypeEnum.NursePractitioner] as string[]).includes(
      user?.physician_type ?? PhysicianPhysicianTypeEnum.NursePractitioner,
    )
  );
};

export const Get =
  <K extends string>(keypath: K) =>
  (state: RootState) =>
    getAtKeypath(state, keypath);

export const IsPatient = (state: RootState) => {
  return state.authentication.role === UserRoleEnum.Patient;
};

export const IsAdmin = (state: RootState) => {
  return state.authentication.role === UserRoleEnum.Admin;
};

export const IsPhysician = (state: RootState) => {
  return state.authentication.role === UserRoleEnum.Physician;
};

export const IsManager = (state: RootState) => {
  return state.authentication.role === UserRoleEnum.Manager;
};

/// returns MedTracker Notifications for relevant accounts
export const GetUserAndLinkedMedicationReminderNotifications = createSelector(
  (state: RootState) => state.authentication.activeUserId,
  AuthenticatedPatient,
  (state: RootState) => state.healthRecords.byId,
  (activeUserId, authPatient, hr) => {
    const takes = _.memoize(
      (notification: MedicationReminderNotification) =>
        new Set(
          (hr[notification.patientId]?.medicationReminders?.[notification.reminderId]?.takes ?? [])
            .filter((it) => it.value === 'taken')
            .map((it) => it.expected_date || it.date),
        ),
    );
    const isDiscontinued = (notification: MedicationReminderNotification) => {
      const rems = hr?.[notification.patientId]?.medicationReminders;
      const rem = rems?.[notification.reminderId];
      return rem && rem.discontinued_since && rem.discontinued_since < notification.date;
    };
    const isNotDiscontinuedOrHasTake = (notification: MedicationReminderNotification) => {
      const val = !isDiscontinued(notification) || takes(notification).has(notification.date);
      return val;
    };
    const isActiveMaster = activeUserId === authPatient?.id;
    const ids = isActiveMaster
      ? [activeUserId, ...(authPatient?.medication_tracker_settings?.follow_linked_accounts ?? [])]
      : [activeUserId];
    const allNotifications = _(ids)
      .filter()
      .flatMap((userId) => hr[userId!]?.medicationReminderNotifications ?? [])
      .filter(isNotDiscontinuedOrHasTake)
      .filter()
      .value();
    return groupMedicationReminderNotifications(allNotifications);
  },
);

/// returns MedTracker Notifications for relevant accounts
export const GetUserAndLinkedMedicationReminders = createSelector(
  (state: RootState) => state.authentication.activeUserId,
  AuthenticatedPatient,
  (state: RootState) => state.healthRecords.byId,
  (activeUserId, authPatient, hr) => {
    const isActiveMaster = activeUserId === authPatient?.id;
    const ids = isActiveMaster
      ? [activeUserId, ...(authPatient?.medication_tracker_settings?.follow_linked_accounts ?? [])]
      : [activeUserId];
    const reminders: PatientData['medicationReminders'] = {};
    _(ids)
      .filter()
      .map((id) => hr[id!]?.medicationReminders ?? {})
      .flatMap((it) => Object.values(it))
      .filter()
      .forEach((reminder) => {
        reminders[reminder!.id!] = reminder!;
      });
    return reminders;
  },
);

export type PatientMedicationWithPatientId = {
  patientId: string;
  medication: PatientMedication;
};

export const GetPatientMedicationForMedicationTrackerLinkedAccounts = createSelector(
  (state: RootState) => state.authentication.activeUserId,
  AuthenticatedPatient,
  (state: RootState) => state.healthRecords.byId,
  (state: RootState) => state.patients.byId,
  (activeUserId, authPatient, hr, patients) => {
    const ids = [
      activeUserId,
      ...(authPatient?.medication_tracker_settings?.follow_linked_accounts ?? []),
    ];
    const res = _(ids)
      .compact()
      .flatMap((id) =>
        (hr[id]?.healthRecords?.medications ?? [])
          .map(
            (med) =>
              ({
                patientId: id,
                medication: med,
              }) as PatientMedicationWithPatientId,
          )
          .filter(
            (med) =>
              !patients[med.patientId]?.medication_tracker_settings?.dismiss_medications?.includes(
                med.medication.id!,
              ),
          ),
      )
      .filter(
        (it) =>
          CPPUtils.isActiveMedication(it.medication) &&
          !!it.medication.value.prescription_medication,
      )
      .orderBy(['value.date'], ['desc'])
      .value();
    return res;
  },
);

export const PendingTemplateRequestsCount = (state: RootState) => {
  if (!state.authentication.permissions.includes(PERMISSIONS.super_admin)) {
    return 0;
  }
  return state.templates.templates.reduce((acc, template) => {
    return acc + (template?.is_pending ? 1 : 0);
  }, 0);
};

export const TotalUnreadCount = (patientId: string) => (state: RootState) => {
  return (
    (state.chat.unreadCount[patientId] || 0) +
    (state.healthRecords.byId[patientId]?.unreadCount || 0)
  );
};

export const TotalUnreadCountWithLinked = createSelector(
  ActivePatient,
  (state: RootState) => state.healthRecords.byId,
  (state: RootState) => state.chat.unreadCount,
  (activePatient, hr, chatUnread) => {
    if (!activePatient) {
      return 0;
    }
    const ids = _.compact([
      activePatient.id,
      ...(activePatient.linked_accounts?.map((it) => it.id) || []),
    ]);

    return ids.reduce((acc, pid) => {
      return acc + (chatUnread[pid] || 0) + (hr[pid]?.unreadCount || 0);
    }, 0);
  },
);

export const UnreadCountLinked = createSelector(
  ActivePatient,
  (state: RootState) => state.healthRecords.byId,
  (state: RootState) => state.chat.unreadCount,
  (activePatient, hr, chatUnread) => {
    if (!activePatient) {
      return 0;
    }
    const ids = [...(activePatient.linked_accounts?.map((it) => it.id) || [])];

    return ids.reduce((acc, pid) => {
      return acc + (chatUnread[pid] || 0) + (hr[pid]?.unreadCount || 0);
    }, 0);
  },
);

export const GetStaffWithRole = createSelector(
  (state: RootState) => state.physicians.byId,
  (state: RootState) => state.admins.byId,
  (_: unknown, id: string | undefined) => id,
  (physicians, admins, id) => {
    if (!id) {
      return {};
    }
    if (physicians[id]) {
      return { role: UserRoleEnum.Physician, user: physicians[id] };
    }
    if (admins[id]) {
      return { role: UserRoleEnum.Admin, user: admins[id] };
    }
    return {};
  },
);

export const AssistantUnreadTotal = (state: RootState) =>
  state.ui.assistants.reduce((acc, n) => acc + n.unread, 0);

export const AppointmentConcern = (concernId: string) => (state: RootState) =>
  _(state.appointments.options)
    .map((it) => getConcern(concernId, it))
    .compact()
    .first();

function getConcern(concernId: string, option: BookingFlowOption) {
  return option.concerns?.find((it) => it.id === concernId);
}

export const IsStripeEnabled = (state: RootState) => {
  return state.authentication.settings?.modules?.stripe?.enabled ?? false;
};

export const IsCycleTrackingEnabled = (state: RootState) => {
  return state.authentication.settings?.modules?.cycleTracking?.enabled ?? false;
};

// Carey chat availability: the build-time flag stays as the deployment kill
// switch; the tenant module is the per-tenant opt-in.
export const IsCareyChatEnabled = (state: RootState) => {
  return (
    !!config.CAREY_ENABLED && (state.authentication.settings?.modules?.careyChat?.enabled ?? false)
  );
};

// Referral program: enabled + credit amount come from the tenant's
// referralProgram module (AppSettings public modules). The $50 fallback
// matches the backend's legacy default.
export const IsReferralProgramEnabled = (state: RootState) => {
  const module = state.authentication.settings?.modules?.referralProgram;
  // Absent module = stale persisted settings (they only refresh at login) or
  // a tenant without the program. Default OPEN: referrer codes are only ever
  // minted while the program is enabled backend-side, so patients of
  // non-program tenants never pass the referrer_code checks anyway — while an
  // explicitly disabled program arrives as { enabled: false } and still hides.
  return module ? !!module.enabled : true;
};

export const ReferralCreditAmount = (state: RootState) => {
  return state.authentication.settings?.modules?.referralProgram?.credit_amount ?? 50;
};

export const TenantId = (state: RootState) => {
  return (
    state.authentication.decodedToken.tenant_id ??
    state.device.clientTenant ??
    getPortalData()?.tenant_id
  );
};
